import { useState, useEffect } from "react"
import { Loader2, Calendar, Trophy, MapPin, Tag } from "lucide-react"
import SwimmerSidebar from "../components/SwimmerSidebar"
import EmptyState from "../components/EmptyState"
import RegistrationModal from "../components/RegistrationModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function SwimmerEvents() {
  const [events, setEvents] = useState([])
  const [registrations, setRegistrations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const { t } = useLanguage()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedRegistration, setSelectedRegistration] = useState(null)

  const handleOpenModal = (evt, reg) => {
    setSelectedEvent(evt)
    setSelectedRegistration(reg)
    setIsModalOpen(true)
  }

  const handleRegisterSuccess = () => {
    setIsModalOpen(false)
    // Trigger refetch by updating refreshKey
    setRefreshKey(prev => prev + 1)
  }

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      setError("")
      try {
        const [resEvents, resRegs] = await Promise.all([
          fetch(`${API}/api/events`, { headers: authHeaders() }),
          fetch(`${API}/api/me/registrations`, { headers: authHeaders() })
        ])
        if (!resEvents.ok) throw new Error("Failed to fetch events")
        if (!resRegs.ok) throw new Error("Failed to fetch registrations")
        setEvents(await resEvents.json())
        setRegistrations(await resRegs.json())
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshKey])

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto">

        {/* ── Page Header ── */}
        <div className="px-4 pt-6 pb-0 sm:px-6 sm:pt-8 lg:px-10 lg:pt-10">
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("events_title")}</h1>
          <p className="mt-1 text-sm text-mist-300">{t("events_subtitle")}</p>
        </div>

        {/* ── Content ── */}
        <div className="px-4 py-6 sm:px-6 lg:px-10 lg:py-8 mt-4">
          {error && (
            <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
            </div>
          ) : (
            <EventsGrid events={events} registrations={registrations} onOpenModal={handleOpenModal} t={t} />
          )}
        </div>
      </main>

      <RegistrationModal 
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={selectedEvent}
        existingRegistration={selectedRegistration}
        onRegisterSuccess={handleRegisterSuccess}
      />
    </div>
  )
}

/* ── Events Grid ── */
function EventsGrid({ events, registrations, onOpenModal, t }) {
  if (events.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="h-12 w-12 text-mist-600" />}
        title={t("events_empty")}
        hint={t("events_empty_hint")}
      />
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {events.map(evt => (
        <div
          key={evt.id}
          className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-ink-800/30 transition-all duration-300 hover:border-gold-500/20 hover:bg-ink-800/50 hover:shadow-[0_0_30px_rgba(217,179,108,0.06)]"
        >
          {/* Poster */}
          <div
            className="h-44 w-full bg-cover bg-center"
            style={{ backgroundColor: evt.poster_color || "#1a1f2e", backgroundImage: evt.poster_url ? `url(${evt.poster_url})` : "none" }}
          />

          {/* Body */}
          <div className="flex flex-1 flex-col p-5">
            <h3 className="font-display text-lg font-bold text-paper-0 group-hover:text-gold-100 transition-colors leading-tight">{evt.title}</h3>
            <p className="mt-1 text-[11px] text-mist-500">{t("events_organised")}</p>
            
            <div className="mt-4 flex flex-col gap-2.5">
              {/* Date Row */}
              <div className="flex items-start gap-2 text-sm text-mist-300">
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-mist-400" />
                <div>
                  <p className="font-semibold">{evt.date ? evt.date.slice(0, 10) : t("common_na")} - {evt.end_date ? evt.end_date.slice(0, 10) : (evt.date ? evt.date.slice(0, 10) : t("common_na"))}</p>
                  <p className="text-[10px] text-mist-500">{t("events_register_by")} {evt.date ? evt.date.slice(0, 10) : t("common_na")}</p>
                </div>
              </div>

              {/* Venue Row */}
              <div className="flex items-start gap-2 text-sm text-mist-300">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mist-400" />
                <p className="font-medium">{evt.venue}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`flex items-center gap-1 rounded bg-blue-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-400`}>
                {evt.status}
              </span>
              <span className="flex items-center gap-1 rounded bg-white/5 px-2 py-1 text-[10px] font-semibold text-mist-300 border border-white/10">
                <Tag className="h-3 w-3" />
                {evt.category}
              </span>
              <span className="flex items-center gap-1 rounded bg-white/5 px-2 py-1 text-[10px] font-semibold text-mist-300 border border-white/10">
                <Trophy className="h-3 w-3" />
                {(evt.available_sub_events || []).length} {t("events_num")}
              </span>
            </div>
            {evt.registrations_open ? (() => {
              const myReg = registrations.find(r => r.event_id === evt.id)
              const availableSubs = evt.available_sub_events || []
              const registeredSubIds = myReg ? (myReg.sub_events || []).map(se => se.id) : []
              
              // Consider fully registered if there are NO sub events and they just paid the base fee,
              // or if they have registered for ALL currently available sub-events.
              const isFullyRegistered = myReg && (
                availableSubs.length === 0 || 
                availableSubs.every(se => registeredSubIds.includes(se.id))
              )
              
              if (isFullyRegistered) {
                return (
                  <button
                    onClick={() => onOpenModal(evt, myReg)}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 py-2.5 text-xs font-bold text-emerald-400 transition-colors hover:bg-emerald-500/20"
                  >
                    {t("events_fully_registered")}
                  </button>
                )
              } else if (myReg) {
                return (
                  <button
                    onClick={() => onOpenModal(evt, myReg)}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-gold-500/10 border border-gold-500/20 py-2.5 text-xs font-bold text-gold-400 transition-colors hover:bg-gold-500/20"
                  >
                    {t("events_add_events")}
                  </button>
                )
              } else {
                return (
                  <button
                    onClick={() => onOpenModal(evt, null)}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-gold-500 py-2.5 text-xs font-bold text-ink-900 transition-colors hover:bg-gold-400"
                  >
                    {t("events_register_now")}
                  </button>
                )
              }
            })() : (
              <button
                disabled
                title="Online registration isn't open yet — check back closer to the event."
                className="mt-5 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] py-2.5 text-xs font-semibold text-mist-500"
              >
                {t("events_closed")}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
